import {
  CreateUserModel,
  EmailNotificationUser,
  ModifyUserModel,
  NotificationProfile,
} from './types';

export const userProfile: EmailNotificationUser = {
  username: '',
  emailAddress: '',
  confirmEmail: '',
  passwordConfirm: '',
  name: '',
  contentType: 'text/html',
  primary: true,
  profile: true,
  password: '',
  extProfiles: [
    {
      id: null,
      agency: null,
      name: '',
      enabled: true,
      additionalEmailAddresses: [],
      timeSpans: [],
      allImpactEventTypes: [],
      eventTypeImpacts: {},
      regionlessEventTypes: [],
      extRegions: [],
    },
  ],
};

export const createUser = async (user: EmailNotificationUser) => {
  const userData: CreateUserModel = {
    name: user.name,
    emailAddress: user.emailAddress,
    password: user.password ?? '',
    passwordConfirm: user.passwordConfirm ?? '',
    contentType: user.contentType ?? '',
    extProfiles: user.extProfiles,
  };

  const response = await fetch(
    import.meta.env.VITE_REACT_APP_API_HOST + '/user/create',
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    }
  );
  const ret = await response.json();
  return ret.exception;
};

export const updateUser = async (user: EmailNotificationUser) => {
  const userData: ModifyUserModel = {
    name: user.name,
    contentType: user.contentType ?? '',
    primary: user.primary,
    profile: user.profile,
    subscribed: user.profile || user.primary,
    emailAddress: user.emailAddress,
    username: user.username,
  };

  const response = await fetch(
    import.meta.env.VITE_REACT_APP_API_HOST + '/account/update',
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    }
  );
  const ret = await response.json();
  return ret.exception;
};

export const updateProfileSave = async (
  updatedProfile: NotificationProfile
) => {
  const response = await fetch(
    import.meta.env.VITE_REACT_APP_API_HOST + '/account/update/profile',
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedProfile),
    }
  );
  const ret = await response.json();
  return ret;
};

export const deleteProfile = async (selectedProfile: NotificationProfile) => {
  const response = await fetch(
    import.meta.env.VITE_REACT_APP_API_HOST + `/account/delete/profile/`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(selectedProfile),
    }
  );
  const ret = await response.json();
  return ret;
};

export const resetPassword = async (email: string) => {
  const response = await fetch(
    `${
      import.meta.env.VITE_REACT_APP_API_HOST
    }/user/reset?email=${encodeURIComponent(email)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
  const ret = await response.json();
  return ret;
};

export const changePassword = async (
  currentPassword: string,
  newPassword: string
) => {
  const requestBody = {
    oldPassword: currentPassword,
    newPassword: newPassword,
  };
  const res = await fetch(
    import.meta.env.VITE_REACT_APP_API_HOST + '/account/changePassword',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    }
  );
  const ret = await res.json();
  return ret;
};

/**
 * Validation result for a NotificationProfile
 */
export interface ProfileValidationResult {
  isValid: boolean;
  errors: {
    timeSpans?: string;
    eventTypes?: string;
    regions?: string;
    profileName?: string;
  };
  messages: string[];
}

/**
 * Validates a NotificationProfile based on the same validation logic used in the stepper components
 * @param profile - The NotificationProfile to validate
 * @returns ProfileValidationResult containing validation state and error messages
 */
export const validateNotificationProfile = (
  profile: NotificationProfile | null | undefined,
  existingProfiles?: NotificationProfile[]
): ProfileValidationResult => {
  const result: ProfileValidationResult = {
    isValid: true,
    errors: {},
    messages: [],
  };

  if (!profile) {
    result.isValid = false;
    result.messages.push('Profile is required');
    return result;
  }

  // 1. Profile Name Validation (from ProfileName component)
  const profileName = profile.name?.trim();
  if (!profileName || profileName === '') {
    result.isValid = false;
    result.errors.profileName = 'Profile name cannot be blank';
    result.messages.push(result.errors.profileName);
  } else if (profileName === 'New Profile') {
    result.isValid = false;
    result.errors.profileName = 'Please choose a different profile name';
    result.messages.push(result.errors.profileName);
  } else if (existingProfiles) {
    // Check for duplicates (excluding the current profile by ID)
    const isDuplicate = existingProfiles.some(
      (existingProfile) =>
        existingProfile.id !== profile.id &&
        existingProfile.name?.toLowerCase() === profileName.toLowerCase()
    );
    if (isDuplicate) {
      result.isValid = false;
      result.errors.profileName = 'Profile name already exists';
      result.messages.push(result.errors.profileName);
    }
  }

  // 2. TimeSpans Validation (from TimeSpans component)
  if (!profile.timeSpans || profile.timeSpans.length === 0) {
    result.isValid = false;
    result.errors.timeSpans =
      'Please add at least one time span to receive notifications';
    result.messages.push(result.errors.timeSpans);
  }

  // 3. Event Types Validation (from EventTypeComponent)
  const hasEventTypes =
    profile.allImpactEventTypes.length > 0 ||
    Object.keys(profile.eventTypeImpacts).length > 0 ||
    profile.regionlessEventTypes.length > 0;

  if (!hasEventTypes) {
    result.isValid = false;
    result.errors.eventTypes =
      'Please select at least one event type to receive alerts';
    result.messages.push(result.errors.eventTypes);
  }

  // 4. Regions Validation (from NotificationMap component)
  if (!profile.extRegions || profile.extRegions.length === 0) {
    result.isValid = false;
    result.errors.regions = 'Please create at least one notification region';
    result.messages.push(result.errors.regions);
  } else if (profile.extRegions.length > 15) {
    result.isValid = false;
    result.errors.regions = 'You can create up to 15 regions maximum';
    result.messages.push(result.errors.regions);
  }

  return result;
};

/**
 * Validates multiple NotificationProfiles and returns validation results for each
 * @param profiles - Array of NotificationProfiles to validate
 * @returns Array of ProfileValidationResult objects corresponding to each profile
 */
export const validateNotificationProfiles = (
  profiles: NotificationProfile[]
): ProfileValidationResult[] => {
  return profiles.map((profile) =>
    validateNotificationProfile(profile, profiles)
  );
};

/**
 * Checks if a NotificationProfile is valid (shorthand for validateNotificationProfile)
 * @param profile - The NotificationProfile to validate
 * @param existingProfiles - Optional array of existing profiles for duplicate name checking
 * @returns boolean indicating if the profile is valid
 */
export const isNotificationProfileValid = (
  profile: NotificationProfile | null | undefined,
  existingProfiles?: NotificationProfile[]
): boolean => {
  console.log('running validation for profile:', profile?.name);
  const result = validateNotificationProfile(profile, existingProfiles);
  return result.isValid;
};

export const isProfileNameValid = (
  name: string,
  user: EmailNotificationUser,
  selectedProfileIndex: number
) => {
  if (!name) return false;

  const isBlank = name.trim() === '';
  const isDefaultName = name.trim() === 'New Profile';
  const isDuplicate = user?.extProfiles.some(
    (profile, index) =>
      index !== selectedProfileIndex &&
      profile.name?.toLowerCase() === name.toLowerCase()
  );

  return !isBlank && !isDefaultName && !isDuplicate;
};
